// Stock Control — the stock figures the rest of Inventory relies on
// (client, 2026-10-24). Items with their reorder level and status, the stock
// take that sets book stock to what was counted, batches with expiry (FIFO),
// and the stock reports. Server: src/api-inventory/stock-control.js.
import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, CalendarClock, ClipboardCheck, Download, FileSpreadsheet, Package, Plus, Search, Settings2, Trash2, Upload } from 'lucide-react';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Modal } from '@/shared/components/ui/Modal';
import { Select } from '@/shared/components/ui/Select';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { api } from '@/shared/api/axios';
import { cn, localToday } from '@/shared/utils/utils';
import { formatINR } from '@/shared/utils/currency';
import { useQueryClient } from '@tanstack/react-query';
import { downloadCsv, materialErrorText } from '../hooks/useMaterials';
import {
  useStockItems, useSaveStockSettings, postStockTake, useStockTakes, useBatches,
  useStockSummary, useConsumptionReport, useInwardBySupplier,
  type StockItem, type StockItemsResponse, type StockStatus, type StockTakeLine, type ItemType,
} from '../hooks/useStockControl';
import { FieldLabel, FilterChips, ItemNameInput, Panel, Pill, fmtDateTime, fmtDay, fmtQty, type PillTone } from './materialShared';

const STATUS: Record<StockStatus, [string, PillTone]> = {
  ok: ['In stock', 'success'],
  reorder: ['Reorder', 'warning'],
  critical: ['Below safety stock', 'danger'],
  negative: ['Below zero', 'danger'],
  out: ['Out of stock', 'neutral'],
};
type View = 'items' | 'take' | 'batches' | 'reports';
type ItemFilter = 'all' | 'in_stock' | 'below' | 'negative' | 'unset';

export function StockControlTab() {
  const [view, setView] = useState<View>('items');
  const [takePrefill, setTakePrefill] = useState<StockItem[] | null>(null);
  return (
    <div className="space-y-4">
      <FilterChips<View>
        value={view}
        onChange={setView}
        options={[
          { value: 'items', label: 'Items & reorder' },
          { value: 'take', label: 'Stock take' },
          { value: 'batches', label: 'Batches & expiry' },
          { value: 'reports', label: 'Reports' },
        ]}
      />
      {view === 'items' && <ItemsView onCount={(items) => { setTakePrefill(items); setView('take'); }} />}
      {view === 'take' && <StockTakeView prefill={takePrefill} onPrefillUsed={() => setTakePrefill(null)} />}
      {view === 'batches' && <BatchesView />}
      {view === 'reports' && <ReportsView />}
    </div>
  );
}

async function fetchAllItems(filters: { q: string; type: string; status: string }) {
  const all: StockItem[] = [];
  for (let page = 1; page < 200; page += 1) {
    const { data } = await api.get<StockItemsResponse>('/api/inventory/stock-control/items', {
      params: { q: filters.q || undefined, type: filters.type !== 'all' ? filters.type : undefined, status: filters.status, page },
    });
    all.push(...data.items);
    if (all.length >= data.total || !data.items.length) break;
  }
  return all;
}

// ── Items & reorder ──────────────────────────────────────────────────────────

function ItemsView({ onCount }: { onCount: (items: StockItem[]) => void }) {
  const [status, setStatus] = useState<ItemFilter>('all');
  const [type, setType] = useState('all');
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<StockItem | null>(null);
  const [bulk, setBulk] = useState(false);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { const t = setTimeout(() => { setDebouncedQ(q); setPage(1); }, 300); return () => clearTimeout(t); }, [q]);
  useEffect(() => { setPage(1); }, [status, type]);
  const { data, isLoading } = useStockItems({ q: debouncedQ, type, status, page });
  const c = data?.counts;
  const pages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  const exportAll = async () => {
    setBusy(true);
    try {
      const all = await fetchAllItems({ q: debouncedQ, type, status });
      downloadCsv(`stock-${status}-${localToday()}.csv`, ['Item', 'SKU', 'Type', 'Unit', 'On hand', 'Reorder level', 'Safety stock', 'Lead time (days)', 'Status', 'Suggested order', 'Value (₹)'],
        all.map((i) => [i.name, i.sku, data?.itemTypes[i.itemType] || i.itemType, i.stockUnit, i.onHand, i.reorderPoint || '', i.safetyStock || '', i.leadTimeDays || '', STATUS[i.status][0], i.suggestedOrder || '', i.value]));
    } catch (err) { toast.error(materialErrorText(err, 'Export failed.')); } finally { setBusy(false); }
  };
  const countThese = async () => {
    setBusy(true);
    try { onCount(await fetchAllItems({ q: debouncedQ, type, status })); } catch (err) { toast.error(materialErrorText(err, 'Could not load the items.')); } finally { setBusy(false); }
  };

  return (
    <Panel
      title="Items & reorder levels"
      subtitle="Stock on hand against each item's reorder level. Set the level once per item; Low Stock Alerts and the suggested order follow it."
      actions={<>
        {data?.canEdit && <Button onClick={() => setAdding(true)}><Plus className="h-4 w-4" /> New material</Button>}
        <Button variant="outline" onClick={exportAll} loading={busy}><Download className="h-4 w-4" /> Excel</Button>
        {data?.canEdit && debouncedQ.trim().length >= 2 && <Button variant="outline" onClick={() => setBulk(true)} disabled={!data?.total}><Settings2 className="h-4 w-4" /> Classify these ({data?.total ?? 0})</Button>}
        {data?.canEdit && <Button variant="outline" onClick={countThese} loading={busy} disabled={!data?.total}><ClipboardCheck className="h-4 w-4" /> Count these {data ? `(${data.total})` : ''}</Button>}
      </>}
    >
      {!!c?.negative && (
        <div className="mx-5 mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-danger/25 bg-danger/10 px-3 py-2 text-body-sm text-danger-text">
          <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
          <span><b>{c.negative} items show stock below zero.</b> Dispatch has been deducting stock that was never entered. Count them and post a stock take to set the real figures.</span>
          {data?.canEdit && <Button size="sm" variant="outline" className="ml-auto" onClick={() => setStatus('negative')}>Show them</Button>}
        </div>
      )}
      <div className="flex flex-wrap items-end justify-between gap-3 px-5 py-3">
        <FilterChips<ItemFilter>
          value={status}
          onChange={setStatus}
          options={[
            { value: 'all', label: 'All', count: c?.all },
            { value: 'in_stock', label: 'In stock', count: c?.inStock },
            { value: 'below', label: 'Below reorder', count: c ? c.reorder + c.critical : undefined },
            { value: 'negative', label: 'Below zero', count: c?.negative },
            { value: 'unset', label: 'No reorder level' },
          ]}
        />
        <div className="flex flex-wrap items-end gap-2">
          <div className="w-48"><Select value={type} onChange={(e) => setType(e.target.value)} options={[{ value: 'all', label: 'All types' }, ...Object.entries(data?.itemTypes || {}).map(([value, label]) => ({ value, label }))]} /></div>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-on-surface-subtle" aria-hidden />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Item or SKU…" className="h-9 w-56 rounded-lg border border-input bg-surface pl-8 pr-3 text-body-sm outline-none focus:border-primary" />
          </div>
        </div>
      </div>
      {isLoading ? <div className="p-5"><Skeleton className="h-60" /></div> : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1000px] text-body-sm">
            <thead className="border-y border-border bg-surface-container-low/60 text-left text-label-caps uppercase text-on-surface-variant">
              <tr>
                <th className="px-5 py-2.5">Item</th>
                <th className="px-3 py-2.5">Type</th>
                <th className="px-3 py-2.5 text-right">On hand</th>
                <th className="px-3 py-2.5 text-right">Reorder level</th>
                <th className="px-3 py-2.5 text-right">Safety</th>
                <th className="px-3 py-2.5 text-right">Lead time</th>
                <th className="px-3 py-2.5">Status</th>
                <th className="px-3 py-2.5 text-right">Order now</th>
                <th className="px-5 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {(data?.items || []).map((i) => (
                <tr key={i._id} className="hover:bg-surface-container-low/60">
                  <td className="px-5 py-2.5">
                    <div className="font-medium text-on-surface">{i.name}</div>
                    <div className="text-data-xs text-on-surface-subtle">{[i.sku, i.density ? `${i.density} kg/L` : '', i.shelfLifeMonths ? `${i.shelfLifeMonths} mo shelf life` : '', i.qcRequired ? 'QC at receipt' : ''].filter(Boolean).join(' · ')}</div>
                  </td>
                  <td className="px-3 py-2.5 text-on-surface-variant">{data?.itemTypes[i.itemType]}</td>
                  <td className={cn('px-3 py-2.5 text-right font-mono tabular-nums', i.onHand < 0 ? 'text-danger-text' : 'text-on-surface')}>{fmtQty(i.onHand)} <span className="text-on-surface-subtle">{i.stockUnit}</span></td>
                  <td className="px-3 py-2.5 text-right font-mono tabular-nums text-on-surface-variant">{i.reorderPoint ? fmtQty(i.reorderPoint) : '—'}</td>
                  <td className="px-3 py-2.5 text-right font-mono tabular-nums text-on-surface-variant">{i.safetyStock ? fmtQty(i.safetyStock) : '—'}</td>
                  <td className="px-3 py-2.5 text-right text-on-surface-variant">{i.leadTimeDays ? `${i.leadTimeDays} d` : '—'}</td>
                  <td className="px-3 py-2.5"><Pill tone={STATUS[i.status][1]}>{STATUS[i.status][0]}</Pill></td>
                  <td className="px-3 py-2.5 text-right font-mono tabular-nums font-semibold text-on-surface">{i.suggestedOrder ? fmtQty(i.suggestedOrder) : ''}</td>
                  <td className="px-5 py-2.5 text-right">
                    {data?.canEdit && <Button size="sm" variant="ghost" onClick={() => setEditing(i)} aria-label={`Stock settings for ${i.name}`}><Settings2 className="h-3.5 w-3.5" /></Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!data?.items.length && <div className="px-5 py-12 text-center text-body-sm text-on-surface-variant">No items match.</div>}
          {pages > 1 && (
            <div className="flex items-center justify-between border-t border-border px-5 py-2.5 text-body-sm text-on-surface-variant">
              <span>{data?.total} items · page {page} of {pages}</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
                <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</Button>
              </div>
            </div>
          )}
        </div>
      )}
      <SettingsModal item={editing} itemTypes={data?.itemTypes} units={data?.units || []} onClose={() => setEditing(null)} />
      {adding && <NewMaterialModal units={data?.units || []} onClose={() => setAdding(false)} />}
      {bulk && <BulkModal q={debouncedQ} type={type} total={data?.total || 0} itemTypes={data?.itemTypes} units={data?.units || []} onClose={() => setBulk(false)} />}
    </Panel>
  );
}

function SettingsModal({ item, itemTypes, units, onClose }: { item: StockItem | null; itemTypes?: Record<ItemType, string>; units: string[]; onClose: () => void }) {
  const save = useSaveStockSettings();
  const [f, setF] = useState({ itemType: 'stock_item', stockUnit: '', reorderPoint: '', safetyStock: '', leadTimeDays: '', density: '', shelfLifeMonths: '', qc: 'auto' });
  useEffect(() => {
    if (!item) return;
    setF({
      itemType: item.itemType, stockUnit: item.stockUnit, reorderPoint: item.reorderPoint ? String(item.reorderPoint) : '',
      safetyStock: item.safetyStock ? String(item.safetyStock) : '', leadTimeDays: item.leadTimeDays ? String(item.leadTimeDays) : '',
      density: item.density ? String(item.density) : '', shelfLifeMonths: item.shelfLifeMonths ? String(item.shelfLifeMonths) : '',
      qc: item.qcRequired === null ? 'auto' : item.qcRequired ? 'yes' : 'no',
    });
  }, [item]);
  if (!item) return null;
  const submit = async () => {
    try {
      await save.mutateAsync({
        id: item._id, itemType: f.itemType as ItemType, stockUnit: f.stockUnit,
        reorderPoint: Number(f.reorderPoint) || 0, safetyStock: Number(f.safetyStock) || 0, leadTimeDays: Number(f.leadTimeDays) || 0,
        density: Number(f.density) || 0, shelfLifeMonths: Number(f.shelfLifeMonths) || 0,
        qcRequired: f.qc === 'auto' ? null : f.qc === 'yes',
      });
      toast.success('Stock settings saved');
      onClose();
    } catch (err) { toast.error(materialErrorText(err, 'Could not save.')); }
  };
  return (
    <Modal open onClose={onClose} size="lg" title={`Stock settings — ${item.name}`} footer={(
      <><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={submit} loading={save.isPending}>Save</Button></>
    )}>
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <label className="block"><FieldLabel>Item type</FieldLabel>
            <Select value={f.itemType} onChange={(e) => setF({ ...f, itemType: e.target.value })} options={Object.entries(itemTypes || {}).map(([value, label]) => ({ value, label }))} />
          </label>
          <label className="block"><FieldLabel>Counted in</FieldLabel>
            <Select value={f.stockUnit} onChange={(e) => setF({ ...f, stockUnit: e.target.value })} options={[{ value: '', label: 'Choose unit' }, ...units.map((u) => ({ value: u, label: u }))]} />
          </label>
          <label className="block"><FieldLabel>QC at receipt</FieldLabel>
            <Select value={f.qc} onChange={(e) => setF({ ...f, qc: e.target.value })} options={[{ value: 'auto', label: 'By type (raw material: yes)' }, { value: 'yes', label: 'Always' }, { value: 'no', label: 'Never' }]} />
          </label>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Input label="Reorder level" type="number" min="0" step="any" value={f.reorderPoint} onChange={(e) => setF({ ...f, reorderPoint: e.target.value })} hint="Alert when stock falls to this" />
          <Input label="Safety stock" type="number" min="0" step="any" value={f.safetyStock} onChange={(e) => setF({ ...f, safetyStock: e.target.value })} hint="Buffer kept for delays" />
          <Input label="Supplier lead time (days)" type="number" min="0" step="1" value={f.leadTimeDays} onChange={(e) => setF({ ...f, leadTimeDays: e.target.value })} />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Input label="Density (kg per litre)" type="number" min="0" step="0.001" value={f.density} onChange={(e) => setF({ ...f, density: e.target.value })} hint="For bulk oil: converts KG and litres" />
          <Input label="Shelf life (months)" type="number" min="0" step="1" value={f.shelfLifeMonths} onChange={(e) => setF({ ...f, shelfLifeMonths: e.target.value })} hint="Sets each batch's expiry" />
        </div>
        <p className="rounded-lg bg-surface-container-low px-3 py-2 text-body-sm text-on-surface-variant">
          A simple rule for the reorder level: average daily use × supplier lead time + safety stock. For example 40 a day × 7 days + 120 = 400.
        </p>
      </div>
    </Modal>
  );
}

function NewMaterialModal({ units, onClose }: { units: string[]; onClose: () => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ name: '', itemType: 'packaging', stockUnit: 'NOS', reorderPoint: '', safetyStock: '', leadTimeDays: '', density: '', shelfLifeMonths: '', hsnSac: '' });
  const [busy, setBusy] = useState(false);
  const set = (p: Partial<typeof f>) => setF((x) => ({ ...x, ...p }));
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post<{ item: StockItem }>('/api/inventory/stock-control/items', f);
      toast.success(`Added ${data.item.name} (${data.item.sku})`);
      qc.invalidateQueries({ queryKey: ['inventory'] });
      onClose();
    } catch (err) { toast.error(materialErrorText(err, 'Could not add the material.')); } finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} size="lg" title="New material" footer={(
      <><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={submit} loading={busy} disabled={f.name.trim().length < 3}>Add material</Button></>
    )}>
      <div className="space-y-4">
        <p className="text-body-sm text-on-surface-variant">For things you stock but do not sell: base oil, additives, drums, buckets, caps, seals, labels, cartons. Finished products are added in Products &amp; Catalog.</p>
        <Input label="Name" value={f.name} onChange={(e) => set({ name: e.target.value })} autoFocus placeholder="e.g. 210L HDPE DRUM" />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <label className="block"><FieldLabel>Type</FieldLabel>
            <Select value={f.itemType} onChange={(e) => set({ itemType: e.target.value, stockUnit: e.target.value === 'raw_material' ? 'LTR' : f.stockUnit })} options={[{ value: 'raw_material', label: 'Raw material' }, { value: 'packaging', label: 'Packaging material' }, { value: 'consumable', label: 'Consumables' }]} />
          </label>
          <label className="block"><FieldLabel>Counted in</FieldLabel>
            <Select value={f.stockUnit} onChange={(e) => set({ stockUnit: e.target.value })} options={units.map((u) => ({ value: u, label: u }))} />
          </label>
          <Input label="HSN (optional)" value={f.hsnSac} onChange={(e) => set({ hsnSac: e.target.value })} placeholder="39231090" />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Input label="Reorder level" type="number" min="0" step="any" value={f.reorderPoint} onChange={(e) => set({ reorderPoint: e.target.value })} />
          <Input label="Safety stock" type="number" min="0" step="any" value={f.safetyStock} onChange={(e) => set({ safetyStock: e.target.value })} />
          <Input label="Lead time (days)" type="number" min="0" step="1" value={f.leadTimeDays} onChange={(e) => set({ leadTimeDays: e.target.value })} />
        </div>
        {f.itemType === 'raw_material' && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Input label="Density (kg per litre)" type="number" min="0" step="0.001" value={f.density} onChange={(e) => set({ density: e.target.value })} placeholder="0.86" />
            <Input label="Shelf life (months)" type="number" min="0" step="1" value={f.shelfLifeMonths} onChange={(e) => set({ shelfLifeMonths: e.target.value })} placeholder="24" />
          </div>
        )}
      </div>
    </Modal>
  );
}

function BulkModal({ q, type, total, itemTypes, units, onClose }: { q: string; type: string; total: number; itemTypes?: Record<ItemType, string>; units: string[]; onClose: () => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ itemType: 'raw_material', stockUnit: '', shelfLifeMonths: '', qc: 'keep' });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post<{ matched: number; updated: number }>('/api/inventory/stock-control/items/bulk-settings', {
        q, type: type !== 'all' ? type : undefined, itemType: f.itemType, stockUnit: f.stockUnit || undefined,
        shelfLifeMonths: f.shelfLifeMonths || undefined, ...(f.qc === 'keep' ? {} : { qcRequired: f.qc === 'auto' ? null : f.qc === 'yes' }),
      });
      toast.success(`${data.updated} of ${data.matched} items updated`);
      qc.invalidateQueries({ queryKey: ['inventory'] });
      onClose();
    } catch (err) { toast.error(materialErrorText(err, 'Could not update.')); } finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} size="md" title={`Classify ${total} items matching "${q}"`} footer={(
      <><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={submit} loading={busy}>Apply to {total} items</Button></>
    )}>
      <div className="space-y-3">
        <label className="block"><FieldLabel>Item type</FieldLabel>
          <Select value={f.itemType} onChange={(e) => setF({ ...f, itemType: e.target.value })} options={Object.entries(itemTypes || {}).map(([value, label]) => ({ value, label }))} />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block"><FieldLabel>Counted in (optional)</FieldLabel>
            <Select value={f.stockUnit} onChange={(e) => setF({ ...f, stockUnit: e.target.value })} options={[{ value: '', label: 'Leave as is' }, ...units.map((u) => ({ value: u, label: u }))]} />
          </label>
          <Input label="Shelf life months (optional)" type="number" min="0" step="1" value={f.shelfLifeMonths} onChange={(e) => setF({ ...f, shelfLifeMonths: e.target.value })} />
        </div>
        <label className="block"><FieldLabel>QC at receipt</FieldLabel>
          <Select value={f.qc} onChange={(e) => setF({ ...f, qc: e.target.value })} options={[{ value: 'keep', label: 'Leave as is' }, { value: 'auto', label: 'By type' }, { value: 'yes', label: 'Always' }, { value: 'no', label: 'Never' }]} />
        </label>
        <p className="text-body-sm text-on-surface-variant">Reorder levels stay per item — set those one by one, or on the items that matter most.</p>
      </div>
    </Modal>
  );
}

// ── Stock take ───────────────────────────────────────────────────────────────

type CountRow = { key: number; productId: string | null; itemName: string; book: number | null; counted: string; batch: string };
let rowKey = 1;
const newRow = (p?: Partial<CountRow>): CountRow => ({ key: rowKey++, productId: null, itemName: '', book: null, counted: '', batch: '', ...p });

function StockTakeView({ prefill, onPrefillUsed }: { prefill: StockItem[] | null; onPrefillUsed: () => void }) {
  const qc = useQueryClient();
  const { data: history = [] } = useStockTakes();
  const { data: meta } = useStockItems({ q: '', type: 'all', status: 'all', page: 1 });
  const canPost = !!meta?.canEdit;
  const [date, setDate] = useState(localToday());
  const [note, setNote] = useState('');
  const [rows, setRows] = useState<CountRow[]>([newRow()]);
  const [preview, setPreview] = useState<StockTakeLine[] | null>(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!prefill) return;
    setRows(prefill.length ? prefill.map((i) => newRow({ productId: i._id, itemName: i.name, book: i.onHand })) : [newRow()]);
    setPreview(null);
    onPrefillUsed();
  }, [prefill, onPrefillUsed]);
  const patch = (key: number, p: Partial<CountRow>) => { setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...p } : r))); setPreview(null); };
  const filled = rows.filter((r) => (r.productId || r.itemName.trim()) && r.counted !== '');
  const body = () => ({ date, note, lines: filled.map((r) => (r.productId ? { productId: r.productId, countedQty: Number(r.counted), batchNumber: r.batch } : { itemName: r.itemName, countedQty: Number(r.counted), batchNumber: r.batch })) as never });

  const check = async () => {
    setBusy(true);
    try { setPreview((await postStockTake(body(), true)).lines || []); } catch (err) { toast.error(materialErrorText(err, 'Could not check the count.')); } finally { setBusy(false); }
  };
  const post = async () => {
    setBusy(true);
    try {
      const r = await postStockTake(body(), false);
      toast.success(`${r.stockTake?.number} posted: ${r.stockTake?.increased} up, ${r.stockTake?.decreased} down, ${r.stockTake?.unchanged} unchanged`);
      setRows([newRow()]); setPreview(null); setNote('');
      qc.invalidateQueries({ queryKey: ['inventory'] });
    } catch (err) { toast.error(materialErrorText(err, 'Could not post the stock take.')); } finally { setBusy(false); }
  };
  const importSheet = async (file: File) => {
    try {
      const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
      const json = XLSX.utils.sheet_to_json<Record<string, unknown>>(wb.Sheets[wb.SheetNames[0]], { defval: '' });
      const pick = (r: Record<string, unknown>, names: string[]) => {
        const k = Object.keys(r).find((h) => names.includes(h.trim().toLowerCase()));
        return k ? String(r[k]).trim() : '';
      };
      const list = json.map((r) => newRow({
        itemName: pick(r, ['item', 'item name', 'product', 'name', 'sku']),
        counted: pick(r, ['counted', 'counted qty', 'qty', 'quantity', 'stock', 'physical']),
        batch: pick(r, ['batch', 'batch no', 'lot']),
      })).filter((r) => r.itemName && r.counted !== '');
      if (!list.length) { toast.error('No rows found. Use columns "Item" and "Counted".'); return; }
      setRows(list); setPreview(null);
      toast.success(`${list.length} rows loaded — press Check to compare with the book.`);
    } catch { toast.error('Could not read that file.'); }
  };

  const totalUp = preview?.filter((l) => l.difference > 0).length || 0;
  const totalDown = preview?.filter((l) => l.difference < 0).length || 0;

  return (
    <div className="space-y-4">
      <Panel
        title="Stock take"
        subtitle="Count what is physically in the store and enter it here. The CRM compares it with the book figure and posts the difference, so stock matches the shelf."
        actions={canPost ? <>
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) importSheet(f); e.target.value = ''; }} />
          <Button variant="outline" onClick={() => fileRef.current?.click()}><Upload className="h-4 w-4" /> Import count sheet</Button>
          <Button variant="outline" onClick={() => downloadCsv('stock-count-sheet.csv', ['Item', 'Counted', 'Batch'], [['RBM BASE OIL SN 70 BULK/LOOSE', '12000', ''], ['210L HDPE DRUM', '45', '']])}><FileSpreadsheet className="h-4 w-4" /> Blank sheet</Button>
        </> : undefined}
      >
        {!canPost ? <p className="px-5 py-4 text-body-sm text-on-surface-variant">Only Admin can post a stock take. The history of counts is below.</p> : (
          <div className="space-y-3 p-5">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <Input label="Count date" type="date" value={date} max={localToday()} onChange={(e) => setDate(e.target.value)} />
              <div className="sm:col-span-2"><Input label="Note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Month-end count, store 1" /></div>
            </div>
            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full min-w-[760px] text-body-sm">
                <thead className="bg-surface-container-low/60 text-left text-label-caps uppercase text-on-surface-variant">
                  <tr><th className="px-3 py-2">Item</th><th className="w-28 px-3 py-2 text-right">Book</th><th className="w-32 px-3 py-2">Counted</th><th className="w-40 px-3 py-2">Batch (optional)</th><th className="w-10" /></tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {rows.map((r) => (
                    <tr key={r.key}>
                      <td className="px-3 py-2"><ItemNameInput value={r.itemName} onChange={(n) => patch(r.key, { itemName: n.itemName, productId: n.productId, book: null })} /></td>
                      <td className={cn('px-3 py-2 text-right font-mono tabular-nums', (r.book ?? 0) < 0 ? 'text-danger-text' : 'text-on-surface-variant')}>{r.book === null ? '—' : fmtQty(r.book)}</td>
                      <td className="px-3 py-2"><input type="number" min="0" step="any" value={r.counted} onChange={(e) => patch(r.key, { counted: e.target.value })} className="h-9 w-full rounded-lg border border-input bg-surface px-2 text-right font-mono outline-none focus:border-primary" /></td>
                      <td className="px-3 py-2"><input value={r.batch} onChange={(e) => patch(r.key, { batch: e.target.value })} className="h-9 w-full rounded-lg border border-input bg-surface px-2 text-body-sm outline-none focus:border-primary" /></td>
                      <td className="px-1 py-2">{rows.length > 1 && <button type="button" aria-label="Remove row" onClick={() => { setRows((rs) => rs.filter((x) => x.key !== r.key)); setPreview(null); }} className="rounded p-1.5 text-on-surface-subtle hover:bg-danger/10 hover:text-danger-text"><Trash2 className="h-4 w-4" /></button>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="border-t border-border px-3 py-2"><Button variant="ghost" size="sm" onClick={() => setRows((rs) => [...rs, newRow()])}><Plus className="h-4 w-4" /> Add item</Button></div>
            </div>
            {preview && (
              <div className="rounded-lg border border-border">
                <div className="border-b border-border bg-surface-container-low/60 px-3 py-2 text-body-sm font-medium text-on-surface">Check: {preview.length} items · {totalUp} go up · {totalDown} go down · {preview.length - totalUp - totalDown} unchanged</div>
                <div className="max-h-72 overflow-auto">
                  <table className="w-full text-body-sm">
                    <tbody className="divide-y divide-border">
                      {preview.map((l) => (
                        <tr key={`${l.productId}-${l.itemName}`}>
                          <td className="px-3 py-1.5 text-on-surface">
                            {l.itemName}
                            {l.newItem && <span className="ml-2 rounded bg-info/10 px-1.5 py-0.5 text-data-xs font-semibold text-info-text">New item — added when posted</span>}
                          </td>
                          <td className="px-3 py-1.5 text-right font-mono tabular-nums text-on-surface-variant">{fmtQty(l.bookQty)} → {fmtQty(l.countedQty)}</td>
                          <td className={cn('px-3 py-1.5 text-right font-mono font-semibold tabular-nums', l.difference > 0 ? 'text-success-text' : l.difference < 0 ? 'text-danger-text' : 'text-on-surface-subtle')}>{l.difference > 0 ? '+' : ''}{fmtQty(l.difference)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            <div className="flex flex-wrap items-center justify-end gap-2">
              <span className="mr-auto text-body-sm text-on-surface-variant">{filled.length} item{filled.length === 1 ? '' : 's'} counted</span>
              <Button variant="outline" onClick={check} loading={busy && !preview} disabled={!filled.length}>Check against book</Button>
              <Button onClick={post} loading={busy && !!preview} disabled={!preview || !preview.length}>Post stock take</Button>
            </div>
          </div>
        )}
      </Panel>
      <Panel title="Earlier stock takes">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-body-sm">
            <thead className="border-b border-border bg-surface-container-low/60 text-left text-label-caps uppercase text-on-surface-variant">
              <tr><th className="px-5 py-2.5">Number</th><th className="px-3 py-2.5">Date</th><th className="px-3 py-2.5 text-right">Items</th><th className="px-3 py-2.5">Changed</th><th className="px-3 py-2.5">Posted by</th><th className="px-5 py-2.5">Note</th></tr>
            </thead>
            <tbody className="divide-y divide-border">
              {history.map((h) => (
                <tr key={h._id}>
                  <td className="whitespace-nowrap px-5 py-2.5 font-mono font-semibold text-on-surface">{h.number}</td>
                  <td className="px-3 py-2.5 text-on-surface-variant">{fmtDay(h.dateKey)}</td>
                  <td className="px-3 py-2.5 text-right font-mono">{h.lines.length}</td>
                  <td className="px-3 py-2.5 text-on-surface-variant">{h.increased} up · {h.decreased} down</td>
                  <td className="px-3 py-2.5 text-on-surface-variant">{h.postedByName} · {fmtDateTime(h.createdAt)}</td>
                  <td className="px-5 py-2.5 text-on-surface-variant">{h.note || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!history.length && <div className="px-5 py-8 text-center text-body-sm text-on-surface-variant">No stock take posted yet.</div>}
        </div>
      </Panel>
    </div>
  );
}

// ── Batches & expiry ─────────────────────────────────────────────────────────

function BatchesView() {
  const [days, setDays] = useState<'0' | '30' | '60' | '90'>('0');
  const { data: batches = [], isLoading } = useBatches(Number(days));
  return (
    <Panel
      title="Batches & expiry"
      subtitle="Every receipt is its own batch. Issues use the earliest-expiring batch first, then the oldest (FIFO), so old stock does not sit and degrade."
      actions={<Button variant="outline" disabled={!batches.length} onClick={() => downloadCsv(`batches-${localToday()}.csv`, ['Item', 'Batch', 'Quantity', 'Unit', 'Received', 'Expiry', 'Days left'], batches.map((b) => [b.itemName, b.lotNumber, b.quantity, b.stockUnit, fmtDay(b.receivedAt), fmtDay(b.expiryDate), b.daysToExpiry ?? '']))}><Download className="h-4 w-4" /> Excel</Button>}
    >
      <div className="px-5 py-3">
        <FilterChips<typeof days> value={days} onChange={setDays} options={[{ value: '0', label: 'All batches in stock' }, { value: '30', label: 'Expiring in 30 days' }, { value: '60', label: '60 days' }, { value: '90', label: '90 days' }]} />
      </div>
      {isLoading ? <div className="p-5"><Skeleton className="h-40" /></div> : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-body-sm">
            <thead className="border-y border-border bg-surface-container-low/60 text-left text-label-caps uppercase text-on-surface-variant">
              <tr><th className="px-5 py-2.5">Item</th><th className="px-3 py-2.5">Batch</th><th className="px-3 py-2.5 text-right">In stock</th><th className="px-3 py-2.5">Received</th><th className="px-3 py-2.5">Expiry</th><th className="px-5 py-2.5 text-right">Days left</th></tr>
            </thead>
            <tbody className="divide-y divide-border">
              {batches.map((b) => (
                <tr key={`${b.productId}-${b.lotNumber}`}>
                  <td className="px-5 py-2.5 font-medium text-on-surface">{b.itemName}</td>
                  <td className="whitespace-nowrap px-3 py-2.5 font-mono text-on-surface-variant">{b.lotNumber}</td>
                  <td className="px-3 py-2.5 text-right font-mono tabular-nums">{fmtQty(b.quantity)} <span className="text-on-surface-subtle">{b.stockUnit}</span></td>
                  <td className="px-3 py-2.5 text-on-surface-variant">{fmtDay(b.receivedAt)}</td>
                  <td className="px-3 py-2.5 text-on-surface-variant">{fmtDay(b.expiryDate)}</td>
                  <td className="px-5 py-2.5 text-right">
                    {b.daysToExpiry === null ? <span className="text-on-surface-subtle">—</span>
                      : <Pill tone={b.daysToExpiry < 0 ? 'danger' : b.daysToExpiry <= 30 ? 'warning' : 'neutral'}>{b.daysToExpiry < 0 ? `expired ${-b.daysToExpiry} d ago` : `${b.daysToExpiry} d`}</Pill>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!batches.length && (
            <div className="flex flex-col items-center gap-2 px-5 py-12 text-center">
              <CalendarClock className="h-8 w-8 text-on-surface-subtle" aria-hidden />
              <p className="text-body-sm text-on-surface-variant">{days === '0' ? 'No batches yet — receipts recorded in Material Inward create them.' : 'Nothing expiring in this window.'}</p>
            </div>
          )}
        </div>
      )}
    </Panel>
  );
}

// ── Reports ──────────────────────────────────────────────────────────────────

function ReportsView() {
  const [from, setFrom] = useState(localToday(new Date(Date.now() - 29 * 864e5)));
  const [to, setTo] = useState(localToday());
  const { data: summary } = useStockSummary();
  const { data: consumption = [] } = useConsumptionReport(from, to);
  const { data: bySupplier = [] } = useInwardBySupplier(from, to);
  const types = useMemo(() => (summary ? (Object.keys(summary.byType) as ItemType[]).filter((k) => summary.byType[k].items > 0) : []), [summary]);
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
        {types.map((k) => {
          const t = summary!.byType[k];
          return (
            <div key={k} className="rounded-card border border-border bg-card px-4 py-3 shadow-surface-1">
              <div className="flex items-center justify-between"><span className="text-body-sm font-medium text-on-surface-variant">{summary!.itemTypes[k]}</span><Package className="h-4 w-4 text-on-surface-subtle" aria-hidden /></div>
              <div className="mt-1 font-mono text-[22px] font-semibold tabular-nums text-on-surface">{t.items.toLocaleString('en-IN')}</div>
              <div className="text-data-xs text-on-surface-subtle">{t.inStock} in stock · {t.belowReorder} below reorder{t.negative ? ` · ${t.negative} below zero` : ''}</div>
              {t.value > 0 && <div className="text-data-xs text-on-surface-variant">{formatINR(t.value)} at cost</div>}
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <div className="w-40"><Input type="date" label="From" value={from} max={to} onChange={(e) => setFrom(e.target.value)} /></div>
        <div className="w-40"><Input type="date" label="To" value={to} min={from} max={localToday()} onChange={(e) => setTo(e.target.value)} /></div>
      </div>
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Panel title="Materials issued" subtitle="From material requests, by item" actions={<Button variant="outline" size="sm" disabled={!consumption.length} onClick={() => downloadCsv(`materials-issued-${from}-${to}.csv`, ['Item', 'Quantity', 'Unit', 'Issues', 'Value (₹)', 'Last issued'], consumption.map((r) => [r.itemName, r.quantity, r.stockUnit, r.issues, r.value, fmtDay(r.lastIssuedAt)]))}><Download className="h-4 w-4" /> Excel</Button>}>
          <table className="w-full text-body-sm">
            <tbody className="divide-y divide-border">
              {consumption.map((r) => (
                <tr key={r.productId}><td className="px-5 py-2 text-on-surface">{r.itemName}</td><td className="px-3 py-2 text-right font-mono tabular-nums">{fmtQty(r.quantity)} <span className="text-on-surface-subtle">{r.stockUnit}</span></td><td className="px-5 py-2 text-right text-on-surface-variant">{r.issues} issue{r.issues === 1 ? '' : 's'}</td></tr>
              ))}
            </tbody>
          </table>
          {!consumption.length && <div className="px-5 py-8 text-center text-body-sm text-on-surface-variant">Nothing issued in this period.</div>}
        </Panel>
        <Panel title="Inward by supplier" subtitle="Receipts in this period" actions={<Button variant="outline" size="sm" disabled={!bySupplier.length} onClick={() => downloadCsv(`inward-by-supplier-${from}-${to}.csv`, ['Supplier', 'Receipts', 'Items', 'QC rejected', 'Value (₹)', 'Last receipt'], bySupplier.map((r) => [r.supplierName, r.receipts, r.items, r.rejected, r.value, fmtDay(r.lastReceipt)]))}><Download className="h-4 w-4" /> Excel</Button>}>
          <table className="w-full text-body-sm">
            <tbody className="divide-y divide-border">
              {bySupplier.map((r) => (
                <tr key={r.supplierName}><td className="px-5 py-2 font-medium text-on-surface">{r.supplierName}</td><td className="px-3 py-2 text-right font-mono tabular-nums">{r.receipts} receipt{r.receipts === 1 ? '' : 's'}</td><td className="px-3 py-2 text-on-surface-variant">{r.items} item{r.items === 1 ? '' : 's'}</td><td className="px-5 py-2 text-right">{r.rejected ? <Pill tone="danger">{r.rejected} rejected</Pill> : null}</td></tr>
              ))}
            </tbody>
          </table>
          {!bySupplier.length && <div className="px-5 py-8 text-center text-body-sm text-on-surface-variant">No receipts in this period.</div>}
        </Panel>
      </div>
    </div>
  );
}

export default StockControlTab;
